/**
 * @fileoverview Utilities related to molecular formats
 */

/**
 * Regular expression for one atom line in a mol block.
 * Produces named capturing group for the main components:
 *     ```x, y, z, elem, numbers```.
 * In addition, to support access of the mapindex field, it also captures subgroups of `numbers`:
 *     ```first6numbers, optionalNumber, mapindex```.
 * @type {RegExp}
 */
export const MolAtomLineRegex = getMolAtomLineRegex();

//--- openbabel supports 88 file types, we might as well support them all!
const supportedMoltypes = ['sdf', 'pdb', 'smi', 'inchi', 'cdx', 'mol2', 'cif', 'mol', 'sd', 'xyz', 'cdxml', 'cml'];
const supportedProteinTypes = ['pdb', 'cif'];
const twoDformats = ['smi', 'cdx', 'cdxml', 'inchi'];
const binaryFormats = ['cdx'];
const proteinExportFormats = ['pdb', 'cif', 'mol2'];

export function getSupportedMoltypes() {
    return supportedMoltypes;
}

export function isSupportedMoltype(moltype) {
    return supportedMoltypes.includes(moltype);
}

export function getSupportedProteinTypes() {
    return supportedProteinTypes;
}

export function isSupportedProteinType(type) {
    return supportedProteinTypes.includes(type);
}

export function is2dFormat(format) {
    return twoDformats.includes(format.toLowerCase());
}

export function isBinaryFormat(format) {
    return binaryFormats.includes(format.toLowerCase());
}

export function getProteinExportFormats() { return proteinExportFormats; }
export function isProteinExportFormat(format) {
    return proteinExportFormats.includes(format.toLowerCase());
}

/**
 * SMILES strings can include the molecule name after the structure, separated by whitespace.
 * Sometimes we want only the structure itself, so we need to strip off the whitespace
 * and the molecule name.
 * @param {string} smilesStr
 * @returns {string}
 */
export function removeSmilesMolName(smilesStr) {
    const match = typeof (smilesStr) === 'string' && smilesStr.match(/([^\s]+)[\s]?.*/);
    return (match && match[1]) || null;
}

/**
 * Create a smiles string with a name, replacing an old name if there is one.
 * @param {string} smilesStr
 * @param {string} name
 * @returns{string}
 */
export function makeNamedSmiles(smilesStr, name) {
    const unnamedSmiles = removeSmilesMolName(smilesStr);
    if (unnamedSmiles && name) {
        return `${unnamedSmiles}\t${name}`;
    } else {
        return smilesStr;
    }
}

/**
 * Determine if a molecule that supports 3D has only 2D coordinates
 * This only supports checking mol/sdf for all 0s for z coordinates.
 * @param {MolDataSource} molSource
 */
export function needs3D(molSource) {
    if (!(molSource.molData && (molSource.molFormat === 'mol' || molSource.molFormat === 'sdf'))) { return undefined; } // don't know
    return molfileNeeds3D(molSource.molData);
}

// MDL / MOL utilities
export function molfileNeeds3D(molText) {
    // Previous implementation tried to return early if it found 3 0.0000 Z coordinates,
    // however, a complicated peptide with multiple 0s triggered a false positive.
    // Removing that optimization.
    const lines = getMolAtomLines(molText);
    return lines.length > 0 && lines.every((line) => line.z === 0);
}

/**
 * Change the compound name embedded in the mol text (the first line) to the provided name
 * @param {string} molText The original mol string
 * @param {string} compoundName The new compound name
 * @returns {string}
 */
export function replaceNameInMolText(molText, compoundName) {
    const eol = molText.indexOf('\n');
    return (eol > -1 && compoundName)
        ? compoundName.concat(molText.substr(eol))
        : molText;
}

/**
 * Clear the atom mapping indices from a mol block.
 * These indices are in the 3rd to last column of the mol atom lines. In the MolAtomLineRegex,
 * they are captured by the `mapindex` named capturing group.
 * @param {string} molText a molblock from which to remove atom mapping indices
 * @returns {string} The molblock without atom mapping indices
 */
export function removeAtomMappingFromMolText(molText) {
    const atomLineRe = new RegExp(MolAtomLineRegex, 'gm');
    const zeroMapIndex = '  0';
    const newMolText = molText.replace(atomLineRe, (...captured) => {
        const [whole, x, y, z, element, numbers, first6, optional] = captured;
        let numbersToUse = numbers;
        if (optional) {
            const update = optional.replace(
                /(?<first3>(?:\d| ){3}(?:\d| ){3}(?:\d| ){3})(?<mapindex>(?:\d| ){3})(?<last2>(?:\d| ){3}(?:\d| ){3})/,
                '$<first3>  0$<last2>'
            );
            numbersToUse = `${first6}${update}`;
        }
        return formMolAtomLine({
            x, y, z, element, numbers: numbersToUse,
        });
    });
    return newMolText;
}

/**
 * Form a molfile atom line from components.
 * Minimum: coords, element, numbers strings
 * This can also receive x,y,z numbers which will be correctly formatted into coordinates.
 * @param {{
 *     coords: string?,
 *     x: number?, y: number?, z: number?,
 *     element: string,
 *     numbers: string,
 * }} param0
 * @returns {string}
 */
export function formMolAtomLine({
    coords: coordsIn, x, y, z,
    element,
    numbers,
}) {
    const coords = coordsIn || [x, y, z].map((n) => Number(n).toFixed(4).padStart(10, ' ')).join('');
    return `${coords} ${element}${numbers}`;
}

/**
 * Return a regular expression for one atom line in a mol block.
 * This produces named capturing group for the main components:
 *     ```x, y, z, element, numbers```.
 * In addition, to support access of the mapindex field, it also captures subgroups of `numbers`:
 *     ```first6numbers, optionalNumbers, mapindex```.
 * @returns {RegExp}
 *
 * Mol atom line pattern: xxxxx.xxxxyyyyy.yyyyzzzzz.zzzz aaaddcccssshhhbbbvvvHHHrrriiimmmnnneee
 * Source: MDL (mol, sdf) Format Spec.pdf, located in:
 * https://www.dropbox.com/home/Conifer%20Point/Software%20Development/External%20Documentation
 * x,y,z coordinates; aaa Atom symbol
 * dd mass difference; ccc charge; sss atom stereo parity; hhh H count; bbb stereo care box
 * vvv valence; HHH H0 designator; rrr / iii not used; mmm mapping number; nnn inversion flag
 * eee exact change flag
 */
export function getMolAtomLineRegex() {
    const coordRe = '(?: |-|\\d){5}\\.\\d{4}';
    const eltRe = '[A-Za-z ]{3}';
    const num2Re = '(?:\\d| ){2}';
    const numRe = '(?:\\d| ){3}';
    const coordinates = `(?<x>${coordRe})(?<y>${coordRe})(?<z>${coordRe})`;
    const element = `(?<element>${eltRe})`;
    const first6Numbers = `(?<first6numbers>${num2Re}${numRe}${numRe}${numRe}${numRe}${numRe})`;
    const optionalNumbers = `(?<optionalNumbers>${numRe}${numRe}${numRe}(?<mapindex>${numRe})${numRe}${numRe})?`;
    const numbers = `(?<numbers>${first6Numbers}${optionalNumbers})`;
    const molAtomsRe = `^${coordinates} ${element}${numbers}`;
    return new RegExp(molAtomsRe);
}

/**
 * Parse molfile atoms, returning objects with x, y, z, element, mapindex, and full number strings.
 * @param {string} molString The molfile string
 * @returns {{
 *     x: number, y: number, z: number, element: string, mapindex: number
 *     numbers: string, first6numbers: string, optionalNumbers: string,
 * }[]}
 */
export function getMolAtomLines(molString) {
    const regex = new RegExp(getMolAtomLineRegex(), 'gm');
    const atomLineMatches = [...molString.matchAll(regex)];
    const numberFields = ['x', 'y', 'z', 'mapindex'];
    return atomLineMatches.map((atomLineMatch) => {
        const entry = {};
        for (const [key, valueIn] of Object.entries(atomLineMatch.groups)) {
            if (valueIn == null) continue;
            let value = valueIn.trim();
            if (numberFields.includes(key)) value = Number(value);
            entry[key] = value;
        }
        return entry;
    });
}

/**
 * Build a V2000 mol block from a set of model Atoms (e.g. a fragment pose returned by a
 * data-service query), using each atom's connectivity. Every atom we have — including the
 * hydrogens carried in the pose — is written explicitly, so the depiction renderer never has to
 * infer implicit hydrogens (which it would get wrong on charged atoms, e.g. an ammonium N+, without
 * per-atom formal charges the pose data doesn't carry). The renderer hides hydrogens for a clean
 * 2D depiction. Aromatic bonds are written in their kekulé form so the renderer never has to
 * perceive aromaticity.
 *
 * Intended for depicting fragments whose structure was never fetched into a FragList (the
 * unloaded structure-of-interest case, BP2-2593): for those, smiles/mol from the server is
 * unavailable, but the query response still carries the pose's atoms + bonds.
 *
 * @param {import('BMapsModel/atoms').Atom[]} atoms
 * @param {string} [name] molecule title (first mol-block line)
 * @returns {string|null} a V2000 mol block, or null if there are no atoms to write
 */
export function molBlockForAtoms(atoms, name='fragment') {
    // Include every atom that has an element. Hydrogens are kept and written explicitly — their
    // bonds are encoded on the heavy-atom side in data-service results, so they end up bonded. A
    // stray unbonded hydrogen (if any) is dropped so it can't show up as a disconnected fragment.
    const molAtoms = (atoms || []).filter((atom) => atom?.elem
        && (atom.elem !== 'H' || (atom.bonds && atom.bonds.length > 0)));
    if (molAtoms.length === 0) return null;

    // Mol-block atom references are 1-based indices into the atom block.
    const indexByAtom = new Map(molAtoms.map((atom, i) => [atom, i + 1]));

    // Each Bond is registered on both of its atoms, so dedupe; keep bonds between included atoms.
    const bonds = [...new Set(molAtoms.flatMap((atom) => atom.bonds || []))]
        .filter((bond) => indexByAtom.has(bond.atom1) && indexByAtom.has(bond.atom2));

    const coord = (n) => Number(n || 0).toFixed(4).padStart(10, ' ');
    const field3 = (n) => String(n).padStart(3, ' ');

    const countsLine = `${field3(molAtoms.length)}${field3(bonds.length)}${'  0'.repeat(8)}999 V2000`;
    const atomLines = molAtoms.map((atom) => (
        `${coord(atom.rawX)}${coord(atom.rawY)}${coord(atom.rawZ)} ${atom.elem.padEnd(3, ' ')} 0${'  0'.repeat(11)}`
    ));
    const bondLines = bonds.map((bond) => (
        `${field3(indexByAtom.get(bond.atom1))}${field3(indexByAtom.get(bond.atom2))}${field3(molBlockBondOrder(bond))}  0  0  0  0`
    ));

    return [
        name,
        '  BMaps',
        'Generated by BMaps from a data-service fragment pose',
        countsLine,
        ...atomLines,
        ...bondLines,
        'M  END',
        '',
    ].join('\n');
}

/**
 * Map a model Bond's order to a V2000 bond-block order code (1/2/3, or 4 for aromatic).
 * Regular orders pass through; aromatic/delocalized bonds use their kekulé order when known,
 * else fall back to 4 (aromatic) — which the renderer kekulizes via its 'dearomatize' step.
 * @param {import('BMapsModel/atoms').Bond} bond
 * @returns {number}
 */
function molBlockBondOrder(bond) {
    if (bond.orderOriginal >= 1 && bond.orderOriginal <= 3) return bond.orderOriginal;
    const kekule = bond.kekuleOrderOriginal;
    if (kekule === 1 || kekule === 2) return kekule;
    return 4;
}

// SDF Utilities

/**
 * Make an SDF entry for a single molecule, given a mol string and a properties object.
 * @param {string} molblock
 * @param {object} properties
 * @returns {string}
 */
export function makeOneSDF(molblock, properties={}) {
    const parts = [
        `${molblock.trimEnd()}\n`,
        // SDF props already end with two newlines
        ...Object.entries(properties).map(([name, value]) => oneSdfProp(name, value)),
        '$$$$\n',
    ];
    return parts.join('');
}

/**
 * Format a single SDF property, given a name and value.
 * According to the spec, the value should have a maximum length of 200 characters (not enforced).
 * It's also allowed to add something after the property name's closing bracket.
 * I believe I have seen molecule indices included this way in large SDFs like NP Atlas.
 * This function does not yet support that.
 * @param {string} name
 * @param {string} value
 * @returns {string}
 */
export function oneSdfProp(name, valueIn) {
    // SDF property spec doesn't allow double-newlines, so condense them.
    const value = valueIn.toString()
        .replace(/\r/g, '')
        .replace(/\n\n/g, '\n')
        .trimEnd();
    return `> <${name}>\n${value}\n\n`;
}

/**
 * Produce a csv string
 * @param {object[]} dataRows
 * @param {{ header: string[], colSep: string, rowSep: string }} optionalParams
 */
export function makeCsvString(dataRows, { header=[], colSep=',', rowSep='\n' }={}) {
    // Optional header can be specified in the params, or it could already be in the data rows.
    const rows = (header?.length > 0) ? [header, ...dataRows] : dataRows;

    // Put quotes around values, so spaces and commas don't cause any trouble.
    // Also double-up quotation marks, the CSV way of encoding them.
    const csvQuote = (x) => ((typeof x === 'string') ? `"${x.replace(/"/g, '""')}"` : String(x));
    return rows
        .map((row) => (Array.isArray(row) ? row : Object.values(row)))
        .map((row) => row.map(csvQuote).join(colSep))
        .join(rowSep);
}

/**
 * For a data format that could represent either compound-only or protein/mixed data,
 * determine if it looks like protein data.
 * @param {string} data
 * @param {string} format
 * @returns {boolean}
 */
export function dataLooksLikeProtein(data, format) {
    if (['pdb', 'cif'].includes(format)) {
        return !!data.match(/^ATOM /m);
    } else if (isSupportedProteinType(format)) {
        console.warn(`Protein detection in ${format} data is NYI`);
    }
    return false;
}

/*
 * Attempt to determine the molecular format of a text data representation,
 * based on heuristics of varying rigor.
 *
 * @param {string} data molecule data text
 * @returns {string} the format guess or empty string if it can't find a match
 */
export function guessFormat(data) {
    if (!data) return '';

    // Whole file formats

    // mol / sdf
    if (data.match('V[2-3]000')) {
        if (data.indexOf('$$$$') > -1) {
            return 'sdf';
        } else {
            return 'mol';
        }
    }

    // mol2
    if (data.indexOf('@<TRIPOS>') > -1) {
        return 'mol2';
    }

    // pdb / cif
    if (isCif(data)) return 'cif'; // looks for data_ or loop_
    if (isPdb(data)) return 'pdb'; // looks for ATOM or HETATM

    // xyz / cml / cdxml
    if (isXYZ(data)) return 'xyz';
    if (isCML(data)) return 'cml';
    if (isCDXML(data)) return 'cdxml';

    // Line formats

    // smi / inchi
    const lines = extractLines(data);
    if (isInchiGroup(lines)) return 'inchi';
    if (isSmilesGroup(lines)) return 'smi';

    return '';
}

export function removeCRs(input) {
    // Reminder: don't ever replace \r on binary data!
    return input.replace(/\r/g, '');
}

export function extractLines(input, ignoreRE) {
    let lines = removeCRs(input)
        .split('\n')
        .filter((line) => line);
    if (ignoreRE) {
        lines = lines.filter((line) => !ignoreRE.test(line));
    }
    return lines;
}

export function isSmilesLine(input) {
    // Naive regex attempt for SMILES - just check for valid characters.
    // For atoms, allow everything in periodic table,
    // making sure that the lowercase letters for aromatic atoms are included (bcnops)
    // Disallowed: 'J', 'Q', 'j', 'q', 'w', 'x', 'z'
    const smilesRE = /^([ABCDEFGHIKLMNOPRSTUVWXYZabcdefghiklmnoprstuvy\d()[\]@+\-=#$\\/%:]+)(\s|$)/;
    return smilesRE.test(input.trim());
}

function isInchiLine(input) {
    const inchiRE = /^InChI=/;
    return inchiRE.test(input.trim());
}

function isSmilesGroup(input) {
    const lines = typeof input === 'string' ? extractLines(input) : input;
    if (lines.length === 0) return false;
    return lines.every((line) => isSmilesLine(line));
}

function isInchiGroup(input) {
    const lines = typeof input === 'string' ? extractLines(input) : input;
    if (lines.length === 0) return false;
    return lines.every((line) => isInchiLine(line));
}

function isXYZ(input) {
    // xyz format is <atom count>\n<comment>\n<atom lines ...>
    // xyz atom lines each have the format:
    //     <whitespace?><element><spaces><number><spaces><number><spaces><number><whitespace?>
    // Because xyz may be a flexible format, I chose a generous regex for number:
    //     (-?((\d+)|(\d+\.\d+)|(\.\d+)))         This allows for any of: XXX | XXX.YYY | .YYY
    const xyzAtomLineRE = /^(\s*)([A-Za-z]+)(\s+)(-?((\d+)|(\d+\.\d+)|(\.\d+)))(\s+)(-?((\d+)|(\d+\.\d+)|(\.\d+)))(\s+)(-?((\d+)|(\d+\.\d+)|(\.\d+)))(\s*)$/;

    const lines = input.trim().replace(/\r/, '').split('\n');
    const [countLine, /* ignore comment line */, ...atomLines] = lines;
    const count = Number(countLine);
    if (Number.isNaN(count) || atomLines.length === 0) {
        return false;
    }

    return atomLines.every((line) => line.match(xyzAtomLineRE));
}

function isCML(input) {
    const cmlRE = /<molecule/;
    return cmlRE.test(input);
}

function isCDXML(input) {
    const cdxmlRE = /<fragment/;
    return cdxmlRE.test(input);
}

function isCif(input) {
    const cifRE = /^\s*(data_\w*|loop_)\s*$/m;
    return cifRE.test(input);
}

function isPdb(input) {
    const pdbRE = /^(ATOM {2}|HETATM)/m;
    return pdbRE.test(input);
}

export function isAminoAcidSequence(input) {
    const aaSequence = /^[A-Z-*]+$/; // lazy regex
    return aaSequence.test(input);
}

export function isDnaSequence(input) {
    const dnaSequence = /^[ACGTN\-*]+$/;
    return dnaSequence.test(input);
}

export function isRnaSequence(input) {
    const rnaSequence = /^[ACGUN\-*]+$/;
    return rnaSequence.test(input);
}

export function isTargetSequence(text) {
    return isAminoAcidSequence(text) || isDnaSequence(text) || isRnaSequence(text);
}

export function isCcdCode(input) {
    const ccdSequence = /^(?:[A-Z0-9]{1,3}|[A-Z0-9]{5})$/;
    return ccdSequence.test(input);
}

export function isFasta(input) {
    const lines = extractLines(input, /^;/).map((line) => line.trim());
    if (lines.length === 0) return false; // no sequence lines
    if ((lines.length % 2) !== 0) return false; // odd # of lines - should alternate header, data
    return lines.every((line, i) => {
        if (i % 2 === 0) {
            // Very permissive header line
            return line.startsWith('>');
        } else {
            // Very permissive data
            return true;
        }
    });
}
